/* ============================================================================
   Android app (WebView shell, package com.esyprints.app) integration.
   The app injects `window.Native` and appends "EsyPrintsApp" to the user
   agent. Everything here is a no-op in a normal browser, so callers never
   need their own guards beyond `IS_APP`.
   ============================================================================ */

interface NativeBridge {
  call: (command: string, args?: Record<string, unknown>) => Promise<unknown>;
  on?: (event: string, handler: (data: unknown) => void) => void;
}

declare global {
  interface Window {
    Native?: NativeBridge;
  }
}

// html.is-app is set in index.html before first paint (UA check, or the
// ?app=1 QA preview switch) — read it so both stay in agreement.
export const IS_APP: boolean =
  typeof window !== 'undefined' &&
  (!!window.Native || navigator.userAgent.includes('EsyPrintsApp') || document.documentElement.classList.contains('is-app'));

/** Call a native command; resolves undefined outside the app or on failure. */
export async function nativeCall<T = unknown>(command: string, args?: Record<string, unknown>): Promise<T | undefined> {
  if (typeof window === 'undefined' || !window.Native) return undefined;
  try {
    return (await window.Native.call(command, args)) as T;
  } catch {
    return undefined;
  }
}

export function haptic(style: 'light' | 'medium' | 'heavy' = 'light'): void {
  void nativeCall('haptics.impact', { style });
}

/** Save an image data URL to the phone's Gallery. Returns false outside the app. */
export async function saveToGallery(dataUrl: string, filename: string): Promise<boolean> {
  if (!window.Native) return false;
  const res = await nativeCall('file.saveDataUrl', { dataUrl, filename });
  return res !== undefined;
}

export async function shareNative(text: string, url?: string): Promise<boolean> {
  if (!window.Native) return false;
  const res = await nativeCall('share.text', { text, url: url ?? location.href, title: 'EsyPrints' });
  return res !== undefined;
}

/** Route taps on push notifications to the page carried in data.url (same-origin only). */
export function initNativeEvents(navigate: (path: string) => void): void {
  if (!window.Native?.on) return;
  window.Native.on('push.opened', (data) => {
    const raw = (data as { url?: string } | undefined)?.url;
    if (!raw) return;
    try {
      const u = new URL(raw, location.origin);
      if (u.origin === location.origin) navigate(u.pathname + u.search + u.hash);
    } catch { /* ignore malformed */ }
  });
}
